import { Award, ArrowRight, Calendar, Clock, Users } from "lucide-react";

const ArticleShipHero = ({ details }) => {
  return (
    <section className="relative w-full flex items-center justify-center overflow-hidden py-10 mt-6">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-slate-950">
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950/80" />
      </div>

      {/* Background Gradients */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/10 via-black to-black opacity-90" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.1),transparent_50%)]" />

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-indigo-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-purple-500/5 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 py-12 lg:py-16">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
          {/* Left Column - Main Content */}
          <div className="w-full md:w-7/12 space-y-6 text-center md:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-950/50 border border-blue-500/30 backdrop-blur-sm">
              <div className="relative">
                <div className="absolute -inset-0.5 bg-blue-500 rounded-full animate-ping opacity-40" />
                <Award className="w-4 h-4 text-blue-400 relative" />
              </div>
              <span className="text-xs font-semibold text-blue-400 tracking-wider uppercase">
                Exclusive Off-Campus Program
              </span>
            </div>

            {/* Main Headline - Split into two lines */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                <div className="text-white">Articleship Cohort -</div>
                <div className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  Your Launchpad to Off-Campus Articleships
                </div>
              </h1>

              <h2 className="text-lg text-blue-300 font-medium">
                Experience Articleship Preparation Like Never Before
              </h2>
            </div>

            {/* Description */}
            <p className="text-gray-300 leading-relaxed">
              Set yourself apart from the crowd and crack your dream off-campus
              articleship with the Article Ship Cohort—a high-impact program
              designed for ambitious CA students.
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="flex items-center gap-3 bg-blue-900/20 border border-blue-500/20 rounded-lg px-4 py-3">
                <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-400 text-left">
                    Duration
                  </div>
                  <div className="text-white font-semibold">4 Weeks</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-blue-900/20 border border-blue-500/20 rounded-lg px-4 py-3">
                <Users className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-400 text-left">
                    Limited Seats
                  </div>
                  <div className="text-white font-semibold">10 Students</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-blue-900/20 border border-blue-500/20 rounded-lg px-4 py-3">
                <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-400 text-left">
                    Starts On
                  </div>
                  <div className="text-white font-semibold">
                    {details?.startDate}
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <button
                className="group relative px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:translate-y-[-2px]"
                onClick={() =>
                  window.open("https://forms.gle/MJKFPg6sL45LE4bP8")
                }
              >
                <span className="relative z-10 flex items-center gap-2 text-sm">
                  Secure Your Spot
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>

          {/* Right Column - Pricing Card */}
          <div className="w-full md:w-5/12">
            <div className="relative">
              {/* Card Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur-lg opacity-70"></div>

              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-gray-900/95 via-blue-950/80 to-gray-900/95 border border-blue-500/30 rounded-2xl backdrop-blur-xl overflow-hidden">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 px-6 py-5 border-b border-blue-500/20">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">
                      Program Investment
                    </h3>
                    <div className="px-3 py-1 bg-blue-500/20 rounded-full">
                      <span className="text-xs font-medium text-blue-300">
                        Limited Time
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {/* Price */}
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-white">
                      ₹{details?.price || "999"}
                    </span>
                    <span className="ml-2 text-sm text-gray-400">one-time</span>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-blue-400 text-xs">✓</span>
                      </div>
                      <span className="text-sm text-gray-300">
                        4 Week, Live Sessions
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-blue-400 text-xs">✓</span>
                      </div>
                      <span className="text-sm text-gray-300">
                        Personal Mentorship
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-blue-400 text-xs">✓</span>
                      </div>
                      <span className="text-sm text-gray-300">
                        Interview Preparation
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-blue-400 text-xs">✓</span>
                      </div>
                      <span className="text-sm text-gray-300">
                        Resume Building Workshop
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-blue-400 text-xs">✓</span>
                      </div>
                      <span className="text-sm text-gray-300">
                        Referral Help
                      </span>
                    </li>
                  </ul>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-800">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">
                        Live
                      </div>
                      <div className="text-xs text-gray-500">
                        Interactive Session
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">
                        Expert
                      </div>
                      <div className="text-xs text-gray-500">Mentorship</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">
                        4.9/5
                      </div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-t border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">
                      Only 10 seats available
                    </div>
                    <div className="text-xs text-blue-300">Filling Fast!</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black via-black/80 to-transparent" />
    </section>
  );
};

export default ArticleShipHero;
